import Button from '@/common/components/Button'
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import Modal from '@/common/components/Modal'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { cn } from '@/common/utils/cn'
import { formatCpf } from '@/common/utils/formatters'
import { relationTypeOptions } from '@/features/support/internal/constants/relationTypeOptions.constant'
import { useSupportAvailableUsers } from '@/features/support/internal/hooks/useSupportAvailableUsers'
import {
  internalSupportTicketResponsibleSchema,
  InternalSupportTicketResponsibleValidationSchema,
} from '@/features/support/internal/schemas/create.schema'
import {
  SupportTicketAvailableUser,
  SupportTicketAvailableUserWithRelationType,
} from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronLeftIcon, PlusIcon, SearchIcon, TrashIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface ModalAddUsersProps {
  users: SupportTicketAvailableUserWithRelationType[]
  setUsers: Dispatch<SetStateAction<SupportTicketAvailableUserWithRelationType[]>>
}

export const ModalAddUsers = ({ users, setUsers }: ModalAddUsersProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [tempUsers, setTempUsers] = useState<SupportTicketAvailableUserWithRelationType[]>(users)

  const [searchUsers, setSearchUsers, _, forceNewSearchUsersValue] = useDebounce('')
  const [selectedUser, setSelectedUser] = useState<SupportTicketAvailableUser>()

  const {
    data: availableUsers,
    meta,
    isFetching: isFetchingAvailableUsers,
    fetchNextPage,
    error: availableUsersError = {} as any,
    refetch: refetchAvailableUsers
  } = useSupportAvailableUsers<SupportTicketAvailableUser[]>({
    filters: { search: searchUsers },
  })

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || isFetchingAvailableUsers) {
      return false
    }

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    const haveNextPage = isToFetchNextPage()

    if (haveNextPage) {
      fetchNextPage()
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<InternalSupportTicketResponsibleValidationSchema>({
    resolver: zodResolver(internalSupportTicketResponsibleSchema),
  })

  const onSubmit: SubmitHandler<InternalSupportTicketResponsibleValidationSchema> = (data) => {
    setTempUsers((prevUsers) => [...prevUsers, { ...selectedUser!, relationType: data.relationType!.value }])
    reset()
  }

  const reset = () => {
    forceNewSearchUsersValue('')
    setSelectedUser(undefined)
    setValue('relationType', undefined)
  }

  useEffect(() => {
    if (isOpen) {
      setTempUsers(users)
      reset()
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button
          type="button"
          name='add_linked_user'
        >
          Adicionar usuário
        </Button>
      }
      title="Vincular usuário"
      titleClass="text-2xl text-white-800"
      className="max-w-2xl bg-black/50 backdrop-blur-xl"
      bodyClassName="text-white-800 bg-white-100"
      body={
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-256px)]"
          statusCode={availableUsersError?.status}
          refetchFunction={refetchAvailableUsers}
        >
          {!selectedUser ? (
            <div className="flex w-full flex-col gap-y-4 max-h-[calc(100vh-16rem)]">
              <TextField
                name='user_search'
                LeftIcon={<SearchIcon className="-translate-y-1 translate-x-1" size="18px" />}
                label="Insira o CPF ou nome do usuário"
                placeholder="Pesquisar"
                onChange={(event) => setSearchUsers((event.target as HTMLInputElement).value)}
                className="text-xs"
              />

              {[
                ...tempUsers,
                ...availableUsers?.filter((availableUser) => !tempUsers.find((user) => user.id === availableUser.id)),
              ]?.length ? (
                <>
                  <div
                    ref={containerRef}
                    onScroll={handleScroll}
                    className="flex w-full flex-col gap-y-4 overflow-y-scroll thin-scroll pr-4"
                  >
                    {[
                      ...tempUsers,
                      ...availableUsers?.filter(
                        (availableUser) => !tempUsers.find((user) => user.id === availableUser.id),
                      ),
                    ]?.map((user) => {
                      const alreadyAdded = tempUsers.find((u) => u.id === user.id)

                      return (
                        <button
                          key={user.id}
                          data-testid='select_deselect_linked_user_button'
                          onClick={() => {
                            if (!!alreadyAdded) {
                              return setTempUsers((prevUsers) => prevUsers.filter((u) => u.id !== user.id))
                            }
                            setSelectedUser(user)
                          }}
                          className={cn(
                            'flex w-full text-start gap-x-2 bg-white-100 border border-transparent rounded-lg p-4',
                            {
                              'border-blue-700': alreadyAdded,
                            },
                          )}
                        >
                          <div className="flex w-full flex-col">
                            <p className="text-sm font-light italic">Nome do usuário</p>

                            <p className="w-full text-lg font-bold">{user.full_name}</p>
                          </div>

                          <div className="flex w-full flex-col">
                            <p className="text-sm font-light italic">CPF do usuário</p>

                            <p className="w-full text-lg font-bold">{user.cpf ? formatCpf(user.cpf) : '--'}</p>
                          </div>

                          <div className="my-auto">
                            {!alreadyAdded ? (
                              <PlusIcon className="size-4 shrink-0" />
                            ) : (
                              <TrashIcon className="size-4 shrink-0 text-danger" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>

                  <div className="flex w-[42rem] -m-6 mt-0">
                    <Button
                      type="button"
                      variant="neutral"
                      className="w-full bg-transparent rounded-none rounded-bl-sm py-4"
                      onClick={() => setIsOpen(false)}
                    >
                      Cancelar
                    </Button>

                    <Button
                      type="button"
                      className="w-full rounded-none rounded-br-sm py-4"
                      name='confirm_linked_user'
                      onClick={() => {
                        setUsers(tempUsers)
                        setIsOpen(false)
                      }}
                    >
                      Confirmar
                    </Button>
                  </div>
                </>
              ) : (
                <EmptyDataMessage message="Nenhum usuário encontrado" />
              )}
            </div>
          ) : (
            <div className="flex w-full flex-col gap-y-4">
              <div className="flex items-center gap-x-2">
                <Button
                  variant="link-white"
                  onClick={() => {
                    reset()
                  }}
                >
                  <ChevronLeftIcon />
                </Button>

                <p className="text-lg font-bold">Voltar para busca</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex w-full flex-col gap-y-2 bg-white-100 p-4 rounded-lg">
                  <div className="flex w-full items-start gap-x-2">
                    <div className="flex flex-col">
                      <p className="text-sm font-light italic">Nome do usuário</p>

                      <p className="text-lg font-bold">{selectedUser.full_name}</p>
                    </div>

                    <div className="flex flex-col">
                      <p className="text-sm font-light italic">CPF do usuário</p>

                      <p className="text-lg font-bold">{selectedUser.cpf ? formatCpf(selectedUser.cpf) : '--'}</p>
                    </div>
                  </div>

                  <SelectController
                    label="Responsabilidade"
                    markAsRequired
                    name="relationType"
                    control={control}
                    options={relationTypeOptions}
                    errorMessage={errors.relationType?.message}
                  />
                </div>

                <div className="flex w-[42rem] -m-6 mt-6">
                  <Button
                    type="button"
                    variant="neutral"
                    className="w-full bg-transparent rounded-none rounded-bl-sm py-4"
                    onClick={reset}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="button"
                    name='add_temp_linked_user'
                    className="w-full rounded-none rounded-br-sm py-4"
                    onClick={handleSubmit(onSubmit)}
                  >
                    Confirmar
                  </Button>
                </div>
              </form>
            </div>
          )}
        </ErrorMessageWrapper>
      }
    />
  )
}
